import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import communicationApi from '../../../../../services/communicationApi';
import { ChannelType, Group, MessageComposition, Routing, Template } from '../../../../../types/communication';

export interface MessageCompositionListFilters {
    search: string;
    channel: ChannelType | '';
    groupId: string;
    status: 'ACTIVE' | 'INACTIVE' | '';
}

export const EMPTY_FILTERS: MessageCompositionListFilters = { search: '', channel: '', groupId: '', status: '' };

interface Paging {
    page: number;
    pageSize: number;
    field: string;
    order: 'ASC' | 'DESC';
}

const SORTABLE = ['name'] as const;

export const useMessageCompositionList = (
    { page, pageSize, field, order }: Paging,
    filters: MessageCompositionListFilters,
) => {
    const [rows, setRows] = useState<MessageComposition[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const [channelCounts, setChannelCounts] = useState<Record<string, number>>({});
    const [groups, setGroups] = useState<Group[]>([]);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [routings, setRoutings] = useState<Routing[]>([]);

    const sortField = (SORTABLE as readonly string[]).includes(field) ? (field as (typeof SORTABLE)[number]) : 'name';

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const common = { search: filters.search, status: filters.status, groupId: filters.groupId || undefined };
            const [result, everyChannel] = await Promise.all([
                communicationApi.listMessageCompositions({ ...common, channel: filters.channel, page, pageSize, sortField, sortOrder: order }),
                communicationApi.listMessageCompositions({ ...common, pageSize: 9999 }),
            ]);
            setRows(result.list);
            setTotal(result.totalElements);
            setTotalPages(result.totalPages);
            setChannelCounts(
                everyChannel.list.reduce<Record<string, number>>((acc, m) => ({ ...acc, [m.channel]: (acc[m.channel] ?? 0) + 1 }), {
                    ALL: everyChannel.totalElements,
                }),
            );
        } catch {
            toast.error('Falha ao carregar composições.');
        } finally {
            setLoading(false);
        }
    }, [filters.search, filters.channel, filters.groupId, filters.status, page, pageSize, sortField, order]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        Promise.all([
            communicationApi.listGroups({}),
            communicationApi.listTemplates({ pageSize: 9999 }),
            communicationApi.listRoutings({}),
        ])
            .then(([groupList, templatePage, routingList]) => {
                setGroups(groupList);
                setTemplates(templatePage.list);
                setRoutings(routingList);
            })
            .catch(() => undefined);
    }, []);

    const variableCount = useMemo(() => {
        const byId = new Map(templates.map((t) => [t.id, t]));
        return (meta: MessageComposition) =>
            new Set((meta.templateIds ?? []).flatMap((id) => (byId.get(id)?.variables ?? []).map((v) => v.name))).size;
    }, [templates]);

    const triggers = useCallback(
        (meta: MessageComposition) =>
            routings.filter((r) => (r.targets ?? []).some((t) => t.messageCompositionId === meta.id)),
        [routings],
    );

    const toggleStatus = async (meta: MessageComposition, reason: string) => {
        try {
            await communicationApi.toggleMessageCompositionStatus(meta.id, reason);
            toast.success(meta.status === 'ACTIVE' ? 'Composição desativada.' : 'Composição ativada.');
            load();
        } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Falha ao atualizar status.');
        }
    };

    return {
        rows,
        total,
        totalPages,
        loading,
        channelCounts,
        groups,
        variableCount,
        triggers,
        toggleStatus,
        reload: load,
    };
};
